// lib/api.ts
import axios from 'axios';
import { clearAuthCookies, isSessionValid, decodeJWT } from './utils/jwt';
import Cookies from 'js-cookie';

const api = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple redirects
let isRedirecting = false;

// Helper to determine redirect URL based on user role
function getLoginRedirectUrl(): string {
  const accessToken = Cookies.get('accessToken');
  if (accessToken) {
    const decoded = decodeJWT(accessToken);
    const userRole = decoded?.role?.toUpperCase();
    const adminRoles = ['CASHIER', 'FINANCIAL', 'ADMIN', 'SUPER_ADMIN', 'DECK_HANDLER', 'STANDARD'];
    if (adminRoles.includes(userRole || '')) {
      return '/admin/sign-in';
    }
  }
  return '/sign-in';
}

// Request interceptor to check token validity before making requests
api.interceptors.request.use(
  (config) => {
    // Only check session for authenticated requests (those with Authorization header)
    const hasAuthHeader = config.headers?.Authorization;
    
    if (hasAuthHeader && !isSessionValid()) {
      // Clear expired tokens
      clearAuthCookies();
      // Reject the request
      return Promise.reject(new Error('Session expired'));
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Get redirect URL before clearing cookies (need token to determine role)
      const redirectUrl = getLoginRedirectUrl();
      
      // Clear tokens on 401 response
      clearAuthCookies();
      
      // Redirect to login page immediately (only once)
      if (typeof window !== 'undefined' && !isRedirecting) {
        isRedirecting = true;
        
        // Use window.location for immediate redirect
        window.location.href = redirectUrl;
      }
    }
    
    return Promise.reject(error);
  }
);

// Reset redirect flag when module is reloaded (for development)
if (typeof window !== 'undefined') {
  (window as any).__resetApiRedirectFlag = () => {
    isRedirecting = false;
  };
}

export default api; 